import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Button, Input } from '../../../components';
import { authService } from '../services/authService';

export default function ResetPasswordForm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<{ password?: string; confirm?: string }>({});

  const mutation = useMutation({
    mutationFn: (password: string) => authService.resetPassword(token!, password),
    onSuccess: () => {
      toast.success('Password has been reset successfully');
      navigate('/login');
    },
    onError: (err: any) => {
      const detail = err.response?.data?.detail || 'Failed to reset password';
      toast.error(detail);
    },
  });

  if (!token) {
    return (
      <div className="w-full max-w-md text-center">
        <h1 className="text-2xl font-bold text-ink font-display">Invalid link</h1>
        <p className="mt-2 text-sm text-ink-2">
          This password reset link is invalid or has expired.
        </p>
        <Link
          to="/forgot-password"
          className="mt-6 inline-block text-sm font-medium text-brand hover:text-brand-ink"
        >
          Request a new reset link
        </Link>
      </div>
    );
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: { password?: string; confirm?: string } = {};

    if (newPassword.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    if (newPassword !== confirmPassword) {
      newErrors.confirm = 'Passwords do not match';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    mutation.mutate(newPassword);
  };

  return (
    <div className="w-full max-w-md">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-ink font-display">Set a new password</h1>
        <p className="mt-2 text-sm text-ink-2">
          Enter your new password below.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mt-8 space-y-6">
        <Input
          type="password"
          label="New password"
          placeholder="Minimum 6 characters"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          error={errors.password}
          leftIcon={<LockClosedIcon className="h-5 w-5" />}
          autoComplete="new-password"
          autoFocus
        />

        <Input
          type="password"
          label="Confirm password"
          placeholder="Re-enter your new password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={errors.confirm}
          leftIcon={<LockClosedIcon className="h-5 w-5" />}
          autoComplete="new-password"
        />

        <Button type="submit" className="w-full" isLoading={mutation.isPending}>
          Reset Password
        </Button>

        <p className="text-center text-sm text-ink-2">
          Remember your password?{' '}
          <Link to="/login" className="font-medium text-brand hover:text-brand-ink">
            Sign in
          </Link>
        </p>
      </form>
    </div>
  );
}
